import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Mail, Phone, MapPin, Clock, Calendar, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { insertData, isSupabaseConfigured } from '../lib/supabase';
import BookingModal from '../components/BookingModal';

const contactInfo = [
  {
    icon: Mail,
    title: 'Email',
    value: 'contact@weblax.fr',
    href: 'mailto:contact@weblax.fr',
  },
  {
    icon: Phone,
    title: 'Téléphone',
    value: '+33 6 85 80 20 16',
    href: 'tel:+33685802016',
  },
  {
    icon: MapPin,
    title: 'Adresse',
    value: 'Paris, France',
    href: null,
  },
  {
    icon: Clock,
    title: 'Horaires',
    value: 'Lun - Ven : 9h - 18h',
    href: null,
  },
];

const faqs = [
  {
    q: 'Quel est le délai de réponse ?',
    a: 'Nous répondons généralement sous 24h en jours ouvrés.',
  },
  {
    q: 'Proposez-vous des appels découverte ?',
    a: 'Oui, nous proposons un premier appel gratuit de 30 minutes.',
  },
  {
    q: 'Travaillez-vous à distance ?',
    a: "Oui, nous travaillons avec des clients partout en France et à l'international.",
  },
  {
    q: 'Comment se déroule un projet ?',
    a: 'Découverte, proposition, validation, création, livraison et suivi.',
  },
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const pageTitle =
    'Contact Weblax | Agence web, création de site internet, SEO et accompagnement digital';
  const pageDescription =
    'Contactez Weblax pour votre projet de création de site internet, refonte, SEO, e-commerce ou développement web. Réponse rapide, devis personnalisé et accompagnement sur mesure.';
  const pageUrl = 'https://weblax.fr/contact';
  const pageImage = 'https://weblax.fr/og-image.jpg';

  const contactPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Weblax',
    url: pageUrl,
    description: pageDescription,
  };

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Weblax',
    url: 'https://weblax.fr',
    logo: 'https://weblax.fr/logo.png',
    email: 'contact@weblax.fr',
    telephone: '+33685802016',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+33685802016',
      contactType: 'customer support',
      email: 'contact@weblax.fr',
      areaServed: 'FR',
      availableLanguage: ['French'],
    },
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubjectChange = (value) => {
    setFormData((prev) => ({ ...prev, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.email || !formData.message) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setIsLoading(true);

    try {
      const { error, demo } = await insertData('contacts', {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
        created_at: new Date().toISOString(),
        status: 'new',
      });

      if (error) {
        throw error;
      }

      if (!demo && isSupabaseConfigured) {
        try {
          const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-contact-notification`;

          console.log('Function URL:', functionUrl);
          console.log(
            'Anon key present:',
            !!process.env.REACT_APP_SUPABASE_ANON_KEY
          );

          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              company: formData.company,
              subject: formData.subject,
              message: formData.message,
            }),
          });

          const responseText = await response.text();
          console.log('Edge Function status:', response.status);
          console.log('Edge Function response:', responseText);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status} - ${responseText}`);
          }
        } catch (err) {
          console.error('Erreur envoi email contact', err);
        }
      }

      if (demo) {
        toast.success('Message envoyé ! (Mode démo - Supabase non configuré)');
      } else {
        toast.success('Votre message a bien été envoyé. Nous vous répondrons rapidement.');
      }

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
      });
    } catch (err) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="contact agence web, contact création site internet, devis site web, agence SEO Paris, développement web, refonte site internet, contact Weblax, agence digitale France"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(contactPageStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <div data-testid="contact-page">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28 text-center">
              <p className="weblax-caption text-primary mb-4">Contact</p>
              <h1 className="weblax-heading-display mb-6 max-w-3xl mx-auto">
                Parlons de votre <span className="weblax-gradient-text">projet</span>
              </h1>
              <p className="weblax-body-lg max-w-2xl mx-auto">
                Une question, un projet, une idée ? N&apos;hésitez pas à nous contacter.
                Nous vous répondrons dans les plus brefs délais.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Contact Info */}
              <div className="lg:col-span-1">
                <h2 className="weblax-heading-2 mb-6">Informations de contact</h2>
                <div className="space-y-4 mb-8">
                  {contactInfo.map((info, index) => (
                    <Card key={index} className="weblax-card">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <info.icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{info.title}</p>
                          {info.href ? (
                            <a href={info.href} className="font-medium hover:text-primary transition-colors">
                              {info.value}
                            </a>
                          ) : (
                            <p className="font-medium">{info.value}</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Quick Actions */}
                <div className="space-y-4">
                  <Card className="weblax-card bg-primary/5 border-primary/20">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Prendre rendez-vous</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Réservez un créneau pour un appel découverte de 1h à 2h.
                      </p>

                      <BookingModal />
                    </CardContent>
                  </Card>

                  <Card className="weblax-card">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <MessageSquare className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Besoin d&apos;un devis ?</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Décrivez votre projet et recevez une estimation personnalisée.
                      </p>
                      <Link to="/devis">
                        <Button className="w-full weblax-btn-primary">
                          Demander un devis
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card className="weblax-card">
                  <CardContent className="p-6 md:p-8">
                    <h2 className="weblax-heading-2 mb-6">Envoyez-nous un message</h2>
                    {!isSupabaseConfigured && (
                      <p className="text-sm text-muted-foreground mb-4 bg-muted p-3 rounded-lg"></p>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">Prénom *</Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="Votre prénom"
                            data-testid="contact-firstname"
                            className="weblax-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Nom</Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Votre nom"
                            data-testid="contact-lastname"
                            className="weblax-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="votre@email.com"
                            data-testid="contact-email"
                            className="weblax-input"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+33 6 12 34 56 78"
                            data-testid="contact-phone"
                            className="weblax-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="company">Entreprise</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Nom de votre entreprise"
                            data-testid="contact-company"
                            className="weblax-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Sujet</Label>
                          <Select value={formData.subject} onValueChange={handleSubjectChange}>
                            <SelectTrigger data-testid="contact-subject" className="weblax-input">
                              <SelectValue placeholder="Choisir un sujet" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="site-web">Création de site web</SelectItem>
                              <SelectItem value="application">Application</SelectItem>
                              <SelectItem value="e-commerce">E-commerce</SelectItem>
                              <SelectItem value="seo">SEO / Référencement</SelectItem>
                              <SelectItem value="maintenance">Maintenance</SelectItem>
                              <SelectItem value="autre">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Décrivez votre projet ou votre question..."
                          data-testid="contact-message"
                          className="weblax-input min-h-[150px]"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isLoading}
                        data-testid="contact-submit"
                        className="weblax-btn-primary w-full md:w-auto"
                      >
                        {isLoading ? 'Envoi en cours...' : 'Envoyer le message'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <h2 className="weblax-heading-1 mb-4">Questions rapides</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {faqs.map((faq, index) => (
                <Card key={index} className="weblax-card">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-2">{faq.q}</h4>
                    <p className="text-muted-foreground text-sm">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;